import type { UseFormSetValue } from 'react-hook-form';
import { MatchingOperator } from '../constants.ts';
import type { AudienceForm, AudienceFormGetValuesForPath, GroupsPath } from '../types.ts';
import { getSiblingOperatorRestrictionReason, shouldForceOrOperator } from './marketOperatorRestrictions.ts';

const CRITERIA_LIST_FIELD_NAMES = ['normalized_definition.includes', 'normalized_definition.excludes'] as const;

export type CriteriaListFieldName = (typeof CRITERIA_LIST_FIELD_NAMES)[number];

export type EnforceMarketUnionOperatorsCallOptions = {
  includeSellableMultipartner?: boolean;
};

const SET_OPTS = { shouldValidate: false } as const;

export const groupsPathForList = (fieldName: CriteriaListFieldName): GroupsPath => `${fieldName}.groups`;

const indexedTopGroupFieldName = (fieldName: CriteriaListFieldName, index: number) =>
  `${fieldName}.groups.${index}` as const;

const listOperatorPath = (fieldName: CriteriaListFieldName) => `${fieldName}.operator` as const;

const indexedTopGroupOperatorPath = (fieldName: CriteriaListFieldName, index: number) =>
  `${indexedTopGroupFieldName(fieldName, index)}.operator` as const;

export function applyMarketForcedUnionOperators(params: {
  fieldName: CriteriaListFieldName;
  getValues: AudienceFormGetValuesForPath;
  setValue: UseFormSetValue<AudienceForm>;
  shouldUseRegionFirstFlow: boolean;
  accountId: string;
  includeSellableMultipartner?: boolean;
}): boolean {
  const {
    fieldName,
    getValues,
    setValue,
    shouldUseRegionFirstFlow,
    accountId,
    includeSellableMultipartner = false,
  } = params;

  if (!shouldUseRegionFirstFlow) {
    return false;
  }

  let didChange = false;
  const groupsKey = groupsPathForList(fieldName);
  const groups = getValues(groupsKey);

  const topReason = getSiblingOperatorRestrictionReason(groups ?? [], accountId, includeSellableMultipartner);

  if (shouldForceOrOperator(topReason)) {
    const opPath = listOperatorPath(fieldName);

    if (getValues(opPath) !== MatchingOperator.UNION) {
      setValue(opPath, MatchingOperator.UNION, SET_OPTS);
      didChange = true;
    }
  }

  (groups ?? []).forEach((node, index) => {
    if (!('groups' in node) || !node.groups || node.groups.length <= 1) {
      return;
    }

    const reason = getSiblingOperatorRestrictionReason(node.groups, accountId, includeSellableMultipartner);

    if (!shouldForceOrOperator(reason)) {
      return;
    }

    const opPath = indexedTopGroupOperatorPath(fieldName, index);

    if (getValues(opPath) !== MatchingOperator.UNION) {
      setValue(opPath, MatchingOperator.UNION, SET_OPTS);
      didChange = true;
    }
  });

  return didChange;
}

export function parseCriteriaListFieldNameFromFieldPath(fieldPath: string): CriteriaListFieldName | null {
  for (const criteriaListFieldName of CRITERIA_LIST_FIELD_NAMES) {
    if (fieldPath.startsWith(criteriaListFieldName)) {
      return criteriaListFieldName;
    }
  }

  return null;
}
